// components/AdminMessaging/MessagingAnalytics.tsx
// Analytics dashboard for admin messaging

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMessagingAnalytics } from '../../services/adminMessaging';
import { StatsCard } from '../StatsCard';
import type { MessagingAnalytics as MessagingAnalyticsType } from '../../types';
import './AdminMessaging.css';

type DateRange = '7d' | '30d' | '90d';

export const MessagingAnalytics: React.FC = () => {
    const navigate = useNavigate();
    const [analytics, setAnalytics] = useState<MessagingAnalyticsType | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dateRange, setDateRange] = useState<DateRange>('30d');


    const getDateRange = useCallback((): { start: Date; end: Date } => {
        const end = new Date();
        const start = new Date();

        switch (dateRange) {
            case '7d':
                start.setDate(end.getDate() - 7);
                break;
            case '30d':
                start.setDate(end.getDate() - 30);
                break;
            case '90d':
                start.setDate(end.getDate() - 90);
                break;
        }

        return { start, end };
    }, [dateRange]);

    const loadAnalytics = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const { start, end } = getDateRange();
            const data = await getMessagingAnalytics(start, end);
            setAnalytics(data);
        } catch (err: any) {
            console.error('Failed to load analytics:', err);
            setError(err.message || 'Failed to load analytics');
        } finally {
            setLoading(false);
        }
    }, [getDateRange]);

    useEffect(() => {
        loadAnalytics();
    }, [loadAnalytics]);

    if (loading) {
        return (
            <div className="messaging-container">
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading analytics...</p>
                </div>
            </div>
        );
    }

    if (error || !analytics) {
        return (
            <div className="messaging-container">
                <div className="error-state">
                    <h3>⚠️ Error</h3>
                    <p>{error || 'Failed to load analytics'}</p>
                    <button onClick={loadAnalytics} className="retry-button">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="messaging-container">
            <header className="messaging-header">
                <div className="header-content">
                    <button
                        onClick={() => navigate('/admin-messaging')}
                        className="back-button"
                    >
                        ← Back to Inbox
                    </button>
                    <div>
                        <h1>📊 Messaging Analytics</h1>
                        <p className="header-subtitle">Performance metrics and insights</p>
                    </div>
                </div>
            </header>

            <main className="analytics-main">
                {/* Date Range Selector */}
                <div className="date-range-selector">
                    <button
                        className={`range-button ${dateRange === '7d' ? 'active' : ''}`}
                        onClick={() => setDateRange('7d')}
                    >
                        Last 7 Days
                    </button>
                    <button
                        className={`range-button ${dateRange === '30d' ? 'active' : ''}`}
                        onClick={() => setDateRange('30d')}
                    >
                        Last 30 Days
                    </button>
                    <button
                        className={`range-button ${dateRange === '90d' ? 'active' : ''}`}
                        onClick={() => setDateRange('90d')}
                    >
                        Last 90 Days
                    </button>
                    <button onClick={loadAnalytics} className="refresh-button" title="Refresh Data">
                        🔄 Refresh
                    </button>
                </div>

                {/* Overview Stats */}
                <section className="analytics-section">
                    <h2>Overview</h2>
                    <div className="stats-grid">
                        <StatsCard
                            title="Messages Received"
                            value={analytics.messagesReceived}
                            icon="📨"
                            color="blue"
                            subtitle="Total messages from users"
                        />
                        <StatsCard
                            title="Responses Sent"
                            value={analytics.responsesSent}
                            icon="✉️"
                            color="green"
                            subtitle="Total responses from admins"
                        />
                        <StatsCard
                            title="Response Rate"
                            value={`${analytics.responseRate.toFixed(1)}%`}
                            icon="📊"
                            color="purple"
                            subtitle="Messages that received responses"
                        />
                        <StatsCard
                            title="Avg Response Time"
                            value={`${analytics.avgResponseTime}min`}
                            icon="⏱️"
                            color="yellow"
                            subtitle="Average time to respond"
                        />
                    </div>
                </section>

                {/* Response Types Breakdown */}
                <section className="analytics-section">
                    <h2>Response Types</h2>
                    <div className="simple-breakdown">
                        <div className="breakdown-grid">
                            <div className="breakdown-item">
                                <div className="breakdown-header">
                                    <span>💬 Text</span>
                                    <strong>{analytics.responsesByType.text}</strong>
                                </div>
                                <div className="breakdown-bar">
                                    <div
                                        className="breakdown-fill text"
                                        style={{
                                            width: `${(analytics.responsesByType.text / analytics.responsesSent) * 100}%`
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="breakdown-item">
                                <div className="breakdown-header">
                                    <span>🎙️ Voice</span>
                                    <strong>{analytics.responsesByType.voice}</strong>
                                </div>
                                <div className="breakdown-bar">
                                    <div
                                        className="breakdown-fill voice"
                                        style={{
                                            width: `${(analytics.responsesByType.voice / analytics.responsesSent) * 100}%`
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="breakdown-item">
                                <div className="breakdown-header">
                                    <span>🖼️ Image</span>
                                    <strong>{analytics.responsesByType.image}</strong>
                                </div>
                                <div className="breakdown-bar">
                                    <div
                                        className="breakdown-fill image"
                                        style={{
                                            width: `${(analytics.responsesByType.image / analytics.responsesSent) * 100}%`
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="breakdown-item">
                                <div className="breakdown-header">
                                    <span>🎨 Mixed</span>
                                    <strong>{analytics.responsesByType.mixed}</strong>
                                </div>
                                <div className="breakdown-bar">
                                    <div
                                        className="breakdown-fill mixed"
                                        style={{
                                            width: `${(analytics.responsesByType.mixed / analytics.responsesSent) * 100}%`
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Moderation */}
                {analytics.moderationFlags > 0 && (
                    <section className="analytics-section">
                        <h2>Moderation</h2>
                        <div className="stats-grid">
                            <StatsCard
                                title="Flagged Messages"
                                value={analytics.moderationFlags}
                                icon="⚠️"
                                color="red"
                                subtitle="Messages with moderation issues"
                            />
                        </div>
                    </section>
                )}

                {/* Key Insights */}
                <section className="analytics-section insights-section">
                    <h2>Key Insights</h2>
                    <div className="insights-grid">
                        <div className="insight-card">
                            <span className="insight-icon">📈</span>
                            <div className="insight-content">
                                <h3>Response Rate</h3>
                                <p>
                                    You're responding to {analytics.responseRate.toFixed(0)}% of messages.
                                    {analytics.responseRate >= 80 ? ' Great job!' : ' Try to improve response coverage.'}
                                </p>
                            </div>
                        </div>

                        <div className="insight-card">
                            <span className="insight-icon">⏱️</span>
                            <div className="insight-content">
                                <h3>Response Time</h3>
                                <p>
                                    Average response time is {analytics.avgResponseTime} minutes.
                                    {analytics.avgResponseTime <= 60 ? ' Excellent!' : ' Consider responding faster.'}
                                </p>
                            </div>
                        </div>

                        <div className="insight-card">
                            <span className="insight-icon">💬</span>
                            <div className="insight-content">
                                <h3>Popular Format</h3>
                                <p>
                                    {analytics.responsesByType.text > analytics.responsesByType.voice ? 'Text' : 'Voice'} responses are most common.
                                    Consider diversifying response types for better engagement.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default MessagingAnalytics;
